"use client";

import * as React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  MapPin,
  SlidersHorizontal,
  FolderOpen,
  Upload,
  ClipboardCheck,
  BarChart3,
  DollarSign,
  ListTodo,
  Workflow,
  Settings,
  MapPinned,
} from "lucide-react";

import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarRail,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { isLocalDev } from "@/lib/is-local-dev";

const navigation = [
  { id: "map", label: "Map", icon: MapPin, href: "/dashboard" },
  { id: "configs", label: "Configs", icon: SlidersHorizontal, href: "/dashboard/configs" },
  { id: "collections", label: "Collections", icon: FolderOpen, href: "/dashboard/collections" },
  { id: "import", label: "Import", icon: Upload, href: "/dashboard/import" },
  { id: "review", label: "Review", icon: ClipboardCheck, href: "/dashboard/review" },
  { id: "physical-mapping", label: "Physical Mapping", icon: MapPinned, href: "/dashboard/physical-mapping" },
  { id: "analytics", label: "Analytics", icon: BarChart3, href: "/dashboard/analytics" },
  { id: "costs", label: "Costs", icon: DollarSign, href: "/dashboard/costs" },
  { id: "scan-requests", label: "Scan Requests", icon: ListTodo, href: "/dashboard/scan-requests" },
  { id: "how-it-works", label: "How it Works", icon: Workflow, href: "/dashboard/how-it-works" },
  { id: "settings", label: "Settings", icon: Settings, href: "/dashboard/settings" },
];

export function AppSidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const reviewCount = trpc.locations.reviewCount.useQuery(undefined, {
    refetchInterval: 60_000,
  });

  const isActive = (href: string) => {
    if (href === "/dashboard") {
      return location.pathname === "/dashboard" || location.pathname === "/dashboard/";
    }
    return location.pathname.startsWith(href);
  };

  const getInitials = (email?: string) => {
    if (!email) return "U";
    return email.split("@")[0]?.slice(0, 2).toUpperCase() || "U";
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-border bg-sidebar"
    >
      <SidebarHeader className="border-b border-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/dashboard" className="group">
                <div className="flex aspect-square size-9 items-center justify-center rounded-xl bg-primary text-white">
                  <span className="text-sm font-bold">ES</span>
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-foreground flex items-center gap-1.5">
                    Equip Scout
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Beta</Badge>
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    by equip.sport
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="px-2">
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">
            Navigation
          </SidebarGroupLabel>
          <SidebarMenu>
            {navigation.filter((item) => isLocalDev || item.id !== "configs").map((item) => {
              const active = isActive(item.href);
              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.label}
                    isActive={active}
                    className={`transition-all duration-200 ${
                      active
                        ? "bg-accent/10 text-accent"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <Link to={item.href}>
                      <item.icon className="size-4" />
                      <span>{item.label}</span>
                      {item.id === "review" && (reviewCount.data?.count ?? 0) > 0 && (
                        <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 min-w-[1.25rem] text-center">
                          {reviewCount.data!.count}
                        </Badge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="transition-all duration-200 hover:bg-muted"
            >
              <Link to="/profile">
                <Avatar className="size-8 border border-border">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-accent/20 text-accent text-xs font-medium">
                    {getInitials(user?.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium text-foreground">
                    {user?.user_metadata?.full_name ||
                      user?.email?.split("@")[0]}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user?.email}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}

export default AppSidebar;
